import React from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

/** Wraps ContentStudio so a rendering exception no longer produces a black page.
 *  On reset, we bump `renderKey` so the child ContentStudio remounts from scratch —
 *  this clears any drifted state, cached refs, or half-attached Radix portals. */
export class ContentStudioBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, renderKey: 0 };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("ContentStudio render error:", error, info?.componentStack);
    }
  }

  reset = () => this.setState((s) => ({ error: null, renderKey: s.renderKey + 1 }));

  render() {
    if (this.state.error) {
      const msg = String(this.state.error?.message || this.state.error || "Unknown error");
      return (
        <div
          data-testid="studio-error-boundary"
          className="border border-red-500/40 bg-red-500/5 p-8 text-foreground/85"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle size={22} className="text-red-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <h3 className="font-headings text-xl text-foreground">
                The AI Content Studio hit an unexpected error.
              </h3>
              <p className="mt-2 font-body text-sm text-foreground/70 leading-relaxed">
                Your admin session is still valid. Reset the studio to try again — no data was lost.
                If this keeps happening, try Chrome menu → &ldquo;Never translate this site&rdquo; and hard-refresh (Ctrl+Shift+R).
              </p>
              <pre className="mt-4 max-h-40 overflow-auto bg-background/60 border border-border/60 p-3 font-mono text-xs text-red-300 whitespace-pre-wrap break-words">
                {msg}
              </pre>
              <div className="mt-5">
                <button
                  onClick={this.reset}
                  data-testid="studio-boundary-reset"
                  className="inline-flex items-center gap-2 border border-border/60 hover:border-primary hover:text-primary px-4 py-2 text-xs uppercase tracking-widest transition-all"
                >
                  <RotateCcw size={13} /> Reset the studio
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    // Force a fresh subtree on every reset so no stale DOM refs survive.
    return <React.Fragment key={this.state.renderKey}>{this.props.children}</React.Fragment>;
  }
}
